import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import TransactionForm from '../transactions/TransactionForm';
import DeleteConfirmModal from '../common/DeleteConfirmModal'; // ✅ use the shared one
import { Transaction } from '../../types/Transaction';
import { useAuth } from '../../context/AuthContext';
import { authFetch } from '../../lib/authFetch';

interface TransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transaction?: Transaction | null;
  defaultDate?: string;
  onDeleteClick?: (tx: Transaction) => boolean;
  submitOverride?: (payload: Record<string, unknown>) => Promise<void>;
  disableDateEdit?: boolean;
  disablePaymentEdit?: boolean;
}

export const getDeleteTransactionUrl = (tx: Pick<Transaction, '_id' | 'source'>): string =>
  `/api/ledger/transactions/${tx._id}`;

const TransactionModalWithDelete: React.FC<TransactionModalProps> = ({
  open,
  onClose,
  onSuccess,
  transaction,
  defaultDate,
  onDeleteClick,
  submitOverride,
  disableDateEdit,
  disablePaymentEdit,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { auth } = useAuth();

  if (!open) return null;

  const handleDelete = async () => {
    if (!transaction) return;
    setLoading(true);
    try {
      const url = getDeleteTransactionUrl(transaction);
      const res = await authFetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to delete transaction');
      }

      onClose();
      onSuccess();
    } catch (err) {
      console.error('Error deleting transaction:', err);
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-testid="transaction-modal">
        <DialogTitle>
          {transaction ? 'Edit Transaction' : 'Add Transaction'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <TransactionForm
            transaction={transaction}
            onSuccess={onSuccess}
            defaultDate={defaultDate}
            submitOverride={submitOverride}
            disableDateEdit={disableDateEdit}
            disablePaymentEdit={disablePaymentEdit}
          />
        </DialogContent>

        {transaction && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => {
                if (transaction && onDeleteClick?.(transaction)) return;
                setConfirmOpen(true);
              }}
              variant="contained"
              fullWidth
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : undefined}
              sx={{
                backgroundColor: 'error.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
                fontWeight: 600,
                fontSize: '0.875rem',
                py: 1,
                borderRadius: 1.5,
                textTransform: 'uppercase',
              }}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      <DeleteConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Transaction?"
        description={
          transaction
            ? `Are you sure you want to delete the "${transaction.category}" transaction of $${transaction.amount.toFixed(2)}?`
            : ''
        }
      />
    </>
  );
};

export default TransactionModalWithDelete;
